import type { ReactNode } from "react";
import React, { useState, useMemo } from "react";
import type { Validator } from "react-admin";
import { SimpleForm, TextInput, useNotify } from "react-admin";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import type {
	AdminInventorySkus,
	UpdateinventoryskusSkus,
	UpdateinventoryskusskustypeType,
	AdminXeroItem,
} from "~/api/types.generated.ts";
import type { GetXeroItemsQuery } from "~/api/get-xero-items-query.generated.ts";
import Modal from "~/components/modal.tsx";
import Skeleton from "~/components/skeleton.tsx";
import { useQuery } from "@apollo/client/react";
import getXeroItemsQuery from "~/api/get-xero-items-query.graphql";

type InventorySkusProps = {
	readonly onSave: (input: any) => Promise<void>;
	readonly saving: boolean;
	readonly children?: ReactNode;
	readonly cellValidate?: Validator | Validator[];
	readonly cellStep?: number;
	readonly actionName: string;
	readonly inventorySkus?: AdminInventorySkus;
};

function InventorySkusForm({
	onSave,
	saving,
	children,
	actionName,
	inventorySkus,
}: InventorySkusProps) {
	const arrangedSkus = useMemo(() => {
		return arrangeAdminInventorySkus(inventorySkus?.skus || []);
	}, [inventorySkus]);
	const notify = useNotify();

	const {
		data: xeroData,
		loading: xeroLoading,
		error: xeroError,
	} = useQuery<GetXeroItemsQuery>(getXeroItemsQuery);

	// Create initial values from arrangedSkus
	const initialValues = useMemo(() => {
		const containers = arrangedSkus.containers.reduce(
			(acc, container) => ({
				...acc,
				[container.containerId]: container.skuCode,
			}),
			{},
		);

		const baseplates = arrangedSkus.baseplates.reduce(
			(acc, baseplate) => ({
				...acc,
				// prefix numeric size with "size_"
				[`size_${baseplate.size}`]: baseplate.skuCode,
			}),
			{},
		);

		const other = arrangedSkus.other.reduce(
			(acc, item) => ({
				...acc,
				[item.type]: item.skuCode,
			}),
			{},
		);

		return { containers, baseplates, other };
	}, [arrangedSkus]);

	const [pendingSaveValues, setPendingValues] = useState<any>();

	const onConfirmClick = async () => {
		try {
			if (!xeroData?.xeroItems) {
				setPendingValues(undefined);
				throw new Error("Xero items not loaded yet");
			}

			// Get Xero items for validation
			const xeroItems = xeroData.xeroItems;
			const xeroSkus = new Set(
				xeroItems.map((item: AdminXeroItem) => item.code),
			);

			if (xeroSkus.size === 0) {
				notify(
					"No SKUs could be validated. Please check your Xero connection.",
					{ type: "error" },
				);
				setPendingValues(undefined);
				return;
			}

			// Collect all SKUs to validate
			const allSkus = [
				...Object.values(pendingSaveValues?.containers || {}),
				...Object.values(pendingSaveValues?.baseplates || {}),
				...Object.values(pendingSaveValues?.other || {}),
			].filter(Boolean) as string[];

			// Check if any SKUs are invalid
			const invalidSkus = allSkus.filter((sku) => !xeroSkus.has(sku.trim()));

			if (invalidSkus.length > 0) {
				notify(
					`The following SKUs are not valid in Xero: ${invalidSkus.join(", ")}`,
					{ type: "error" },
				);
				setPendingValues(undefined);
				return;
			}

			const transformedValues = {
				baseplates: arrangedSkus.baseplates.map((baseplate) => ({
					size: baseplate.size,
					skuCode:
						pendingSaveValues?.baseplates?.[`size_${baseplate.size}`] ?? "",
				})),
				containers: arrangedSkus.containers.map((container) => ({
					type: container.type,
					containerId: container.containerId,
					colourId: container.colourId,
					skuCode: pendingSaveValues?.containers?.[container.containerId] ?? "",
				})),
				other: arrangedSkus.other.map((item) => {
					const pendingSkuCode = pendingSaveValues?.other?.[item.type];
					return {
						type: item.type,
						skuCode:
							pendingSkuCode === undefined ? item.skuCode : pendingSkuCode,
					};
				}),
				comment: pendingSaveValues?.comment,
			};
			await onSave(transformedValues);
			setPendingValues(undefined);
		} catch (error: unknown) {
			notify(error instanceof Error ? error.message : String(error), {
				type: "error",
			});
		}
	};

	function SkuFormSkeleton() {
		return (
			<>
				<div
					style={{
						flex: 1,
						display: "flex",
						flexDirection: "row",
						gap: 20,
						marginTop: 20,
						marginBottom: 20,
					}}
				>
					<Skeleton height="40px" width="200px" />
				</div>
			</>
		);
	}

	return (
		<>
			<Modal
				id="pending-save"
				open={!!pendingSaveValues && !saving}
				onClose={() => setPendingValues(undefined)}
				title="Are you sure?"
				description={`This action will ${actionName}. Are you sure you want to continue?`}
				buttons={[
					<Button
						key="no"
						type="button"
						color="secondary"
						variant="outlined"
						onClick={() => setPendingValues(undefined)}
					>
						No
					</Button>,
					<Button
						key="yes"
						type="button"
						color="primary"
						variant="contained"
						onClick={onConfirmClick}
					>
						Yes
					</Button>,
				]}
			/>
			<SimpleForm
				save={(values: Record<string, any>) => {
					if (xeroError) {
						notify("Cannot save while there is an error loading Xero items", {
							type: "error",
						});
						return;
					}
					setPendingValues(values);
				}}
				saving={saving}
				initialValues={initialValues}
				disabled={xeroLoading || !!xeroError}
			>
				{xeroError && (
					<div
						style={{
							color: "#d32f2f",
							marginBottom: "1rem",
							padding: "1.5rem 2rem",
							backgroundColor: "#ffebee",
							borderRadius: "4px",
							border: "1px solid #ffcdd2",
							boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
							width: "100%",
							maxWidth: "100%",
							boxSizing: "border-box",
						}}
					>
						<Typography
							variant="body1"
							style={{ fontWeight: 500, width: "100%" }}
						>
							Error loading Xero items: {xeroError.message}
						</Typography>
					</div>
				)}
				{xeroLoading && (
					<div
						style={{
							marginBottom: "1rem",
							padding: "1rem",
							backgroundColor: "#e3f2fd",
							borderRadius: "4px",
							display: "flex",
							alignItems: "center",
							gap: "1rem",
						}}
					>
						<CircularProgress size={20} />
						<Typography variant="body1">Loading Xero items...</Typography>
					</div>
				)}
				<div
					style={{ display: "flex", flexDirection: "column", width: "100%" }}
				>
					{/* Containers */}
					<Typography component="h3">Containers</Typography>

					{!arrangedSkus.containers.length && <SkuFormSkeleton />}

					<div
						style={{ flex: 1, display: "flex", flexDirection: "row", gap: 20 }}
					>
						{arrangedSkus.containers.map((container) => (
							<div key={container.containerId}>
								<Typography color="textSecondary" component="h4">
									{container.containerId}
								</Typography>
								<TextInput
									source={`containers.${container.containerId}`}
									label={container.containerId}
									helperText={false}
									disabled={saving}
								/>
							</div>
						))}
					</div>

					{/* Baseplates */}
					<Typography component="h3" style={{ marginTop: 20 }}>
						Baseplates
					</Typography>

					{!arrangedSkus.baseplates.length && <SkuFormSkeleton />}

					<div
						style={{
							flex: 1,
							display: "flex",
							flexDirection: "row",
							gap: 20,
							alignItems: "center",
						}}
					>
						{arrangedSkus.baseplates.map((baseplate) => {
							return (
								<div key={baseplate.size}>
									<Typography color="textSecondary" component="h4">
										{baseplate.size}x{baseplate.size}
									</Typography>
									<TextInput
										// now referencing "size_8", "size_16", etc.
										source={`baseplates.size_${baseplate.size}`}
										label={`SKU (${baseplate.size}x${baseplate.size})`}
										helperText={false}
										disabled={saving}
									/>
								</div>
							);
						})}
					</div>

					{/* Other Items */}
					<Typography component="h3" style={{ marginTop: 20 }}>
						Other Items
					</Typography>

					{!arrangedSkus.other.length && <SkuFormSkeleton />}

					<div
						style={{
							flexWrap: "wrap",
							display: "flex",
							flexDirection: "row",
							gap: 20,
						}}
					>
						{arrangedSkus.other.map((other) => (
							<TextInput
								key={other.type}
								style={{ width: "30.33%" }}
								source={`other.${other.type}`}
								label={other.type}
								helperText={false}
								disabled={saving}
							/>
						))}
					</div>
					<Typography component="h3" style={{ marginTop: 20 }}>
						Comment
					</Typography>
					<TextInput
						source="comment"
						style={{ width: "30.33%" }}
						disabled={saving}
					/>
				</div>
				{children}
			</SimpleForm>
		</>
	);
}

type BaseSku = {
	skuCode: string;
};

type BaseplateSku = BaseSku & {
	size: number;
};

type ContainerSku = BaseSku & {
	type: string;
	containerId: string;
	colourId: string;
};

type OtherSku = BaseSku & {
	type: string;
};

type ArrangedSkus = {
	baseplates: BaseplateSku[];
	containers: ContainerSku[];
	other: OtherSku[];
};

function arrangeAdminInventorySkus(
	inventorySkus: readonly UpdateinventoryskusSkus[],
): ArrangedSkus {
	return inventorySkus.reduce<ArrangedSkus>(
		(acc, sku) => {
			const { type, skuCode } = sku;

			if (type.type === "baseplate" && type.size) {
				acc.baseplates.push({
					size: type.size,
					skuCode,
				});
			} else if (
				type.type === "container" &&
				type.containerId &&
				type.colourId
			) {
				acc.containers.push({
					type: type.type,
					containerId: type.containerId,
					colourId: type.colourId,
					skuCode,
				});
			} else if (type.type) {
				acc.other.push({
					type: type.type,
					skuCode,
				});
			}

			return acc;
		},
		{ baseplates: [], containers: [], other: [] },
	);
}

function inventorySkusFormValuesToApi(input: {
	baseplates: Array<{
		size: number;
		skuCode: string;
	}>;
	containers: Array<{
		type: string;
		containerId: string;
		colourId: string;
		skuCode: string;
	}>;
	other: Array<{
		type: string;
		skuCode: string;
	}>;
	comment?: string;
}): { skus: UpdateinventoryskusSkus[]; comment: string | null } {
	const skus: UpdateinventoryskusSkus[] = [
		...input.baseplates.map((bp) => ({
			type: {
				type: "baseplate" as UpdateinventoryskusskustypeType,
				size: bp.size,
				containerId: null,
				colourId: null,
			},
			skuCode: (bp.skuCode ?? "").trim(),
		})),
		...input.containers.map((c) => ({
			type: {
				type: "container" as UpdateinventoryskusskustypeType,
				containerId: c.containerId,
				colourId: c.colourId,
				size: null,
			},
			skuCode: (c.skuCode ?? "").trim(),
		})),
		...input.other.map((o) => ({
			type: {
				type: o.type as UpdateinventoryskusskustypeType,
				containerId: null,
				colourId: null,
				size: null,
			},
			skuCode: (o.skuCode ?? "").trim(),
		})),
	];
	return {
		skus,
		comment: input.comment ?? null,
	};
}

export type { InventorySkusProps };
export { inventorySkusFormValuesToApi };
export default InventorySkusForm;
